import React, { useRef } from 'react';
import { createElementKey, enhanceImageUrl } from 'utils';
import { CardLinkWithGAPromoClick, Section, SectionTitle } from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { strictBreakpoints } from 'common/styles/breakpoints';
import { PromoBanner } from 'modules/model-json';

import { PROMO_ELEMENT_BLOCK } from '../../promo-constants';
import {
  StyledUspCards,
  uspCard,
  uspCardsGridLevelMap,
  upsCardImage,
  uspCardsWrapper,
  uspCardTitle,
} from './usp-cards-styles';

type UspCardsProps = {
  className?: string;
  title: string;
  level0: PromoBanner[];
  level1: PromoBanner[];
  level2: PromoBanner[];
  level3: PromoBanner[];
};

export const UspCards = ({ className, title, ...cardList }: UspCardsProps) => {
  const refs = useRef<HTMLAnchorElement[]>([]);

  let position = -1;
  const flatBannerList = Object.values(cardList).flat();
  const promotions = flatBannerList.map(({ creative }, index) => ({
    name: PROMO_ELEMENT_BLOCK,
    creative,
    position: (index + 1).toString(),
  }));

  usePromoViewEvent(refs, PROMO_ELEMENT_BLOCK, promotions);

  return (
    <Section className={className}>
      {title && <SectionTitle as="h1" className={uspCardTitle}>{title}</SectionTitle>}
      <div className={uspCardsWrapper}>
        {(Object.keys(cardList) as Array<keyof typeof cardList>).map(
          (key, keyIndex) =>
            cardList[key].length > 0 && (
              <StyledUspCards
                cardNumber={cardList[key].length}
                className={uspCardsGridLevelMap[key === 'level0' ? 0 : 1]}
                key={createElementKey('usp-card-level', keyIndex)}>
                {cardList[key].map(
                  ({
                    bannerDesktopRef,
                    bannerLargeRef,
                    bannerMobileRef,
                    bannerTabletRef,
                    bannerUrl,
                    creative,
                  }) => {
                    position += 1;

                    return (
                      <CardLinkWithGAPromoClick
                        bannerRefs={refs}
                        className={uspCard}
                        elementBlock={PROMO_ELEMENT_BLOCK}
                        href={bannerUrl}
                        key={createElementKey('usp-card', position)}
                        position={position}
                        promotion={promotions[position]}
                        title={creative}>
                        <picture>
                          <source
                            srcSet={enhanceImageUrl(bannerLargeRef)}
                            media={strictBreakpoints['--only-large-desktop']}
                          />
                          <source
                            srcSet={enhanceImageUrl(bannerDesktopRef)}
                            media={strictBreakpoints['--only-desktop']}
                          />
                          <source
                            srcSet={enhanceImageUrl(bannerTabletRef)}
                            media={strictBreakpoints['--only-tablet']}
                          />
                          <img
                            alt={creative}
                            className={upsCardImage}
                            loading="lazy"
                            src={enhanceImageUrl(bannerMobileRef)}
                            title={creative}
                          />
                        </picture>
                      </CardLinkWithGAPromoClick>
                    );
                  }
                )}
              </StyledUspCards>
            )
        )}
      </div>
    </Section>
  );
};

UspCards.defaultProps = {
  className: '',
};
